import {
  Button,
  ColumnTable,
  Icon,
  OutlinedSelect,
  Table,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useWarningsSummaryDetailsFormLoaderData } from '../../loaders/StudentWarningsSummaryDetailsFormLoader';
import { useWarningsSummaryDetailsLoader } from '../../loaders/StudentWarningsSummaryDetailsLoader';
import BehaviorWarningGroupModal, {
  BehaviorWarningGroupModalMethods,
  BehaviorWarningGroupRecord,
} from './BehaviorWarningGroupModal';

const BehaviorWarningGroup = () => {
  const data = useWarningsSummaryDetailsFormLoaderData();
  const { result, refresh } = useWarningsSummaryDetailsLoader();
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_DETAILS);
  const modalRef = useRef<BehaviorWarningGroupModalMethods>(null);

  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);

  const items = useMemo(() => {
    if (!result?.success) {
      return [];
    }

    return (result.data?.groupData as BehaviorWarningGroupRecord[]) ?? [];
  }, [result]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (typeFilter && item.type !== typeFilter) {
        return false;
      }
      if (severityFilter && item.severity !== severityFilter) {
        return false;
      }
      return true;
    });
  }, [items, typeFilter, severityFilter]);

  const handleOpenModal = useCallback((record?: BehaviorWarningGroupRecord) => {
    modalRef.current?.open(record);
  }, []);

  const columns = useMemo<ColumnTable<BehaviorWarningGroupRecord>[]>(
    () => [
      {
        columnName: 'type',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.type`),
        cellFormat: ({ value }) => {
          const type = data?.behaviorRecordTypes.find(
            (option) => option.value === value,
          );
          return type ? type.label : value;
        },
      },
      {
        columnName: 'date',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.date`),
        cellFormat: ({ value }) => {
          const date = dayjs(value);
          return date.utc().format('DD/MM/YYYY');
        },
      },
      {
        columnName: 'title',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.title`),
      },
      {
        columnName: 'notes',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.notes`),
      },
      {
        columnName: 'severity',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.severity`),
        cellFormat: ({ value }) => {
          const severity = data?.behaviorRecordSeverities.find(
            (option) => option.value === value,
          );
          return severity ? severity.label : value;
        },
      },
      {
        columnName: 'requiresDetention',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.requiresDetention`),
        cellFormat: ({ value }) => {
          return value
            ? t('group.behavior.table.requiresDetentionYes')
            : t('group.behavior.table.requiresDetentionNo');
        },
      },
      {
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.behavior.table.details`),
        cellFormat: ({ row }) => {
          return (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => handleOpenModal(row)}
            >
              <Icon name="eye" />
            </button>
          );
        },
      },
    ],
    [t, data, handleOpenModal],
  );

  return (
    <div className="w-100 d-flex flex-column mt-3" style={{ gap: 12 }}>
      <Row>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="typeFilter"
            label={t(`group.behavior.table.type`)}
            options={data?.behaviorRecordTypes || []}
            isClearable
            value={
              data?.behaviorRecordTypes.find(
                (option) => option.value === typeFilter,
              ) || undefined
            }
            onChange={(option) => setTypeFilter(option ? option.value : null)}
            disabled={!data?.behaviorRecordTypes.length}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="severityFilter"
            label={t(`group.behavior.table.severity`)}
            options={data?.behaviorRecordSeverities || []}
            isClearable
            value={
              data?.behaviorRecordSeverities.find(
                (option) => option.value === severityFilter,
              ) || undefined
            }
            onChange={(option) =>
              setSeverityFilter(option ? option.value : null)
            }
            disabled={!data?.behaviorRecordSeverities.length}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <h2 className="text-primary fw-700 fs-20 mb-0 mt-3">
            {t(`group.behavior.title`)}
          </h2>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-end">
            <Button
              text={t(`group.behavior.actions.downloadReport`)}
              size="md"
              type="submit"
              icon="download"
            />
            <Button
              text={t(`group.behavior.actions.addRecord`)}
              className="ml-2"
              size="md"
              outlined
              type="button"
              icon="plus"
              onClick={() => handleOpenModal()}
            />
          </div>
        </Col>
      </Row>
      <Table data={filteredItems} columns={columns} />
      <BehaviorWarningGroupModal ref={modalRef} onSave={refresh} />
    </div>
  );
};

export default BehaviorWarningGroup;
